import { Controller } from "@hotwired/stimulus"
import { fetches } from "./mixins/fetches"
import { replaceUrl } from "../turbo/history"

/**
 * DialogController allows to open a dialog and load its content from
 * an endpoint.
 *
 * The structure needs to look like (partly implemented by Dialog::Cell):
 *
 * %div{data: { controller: :dialog, dialog_fallback_hash_value: 'some_tab' }}
 *  %button{type: :button,
 *          data: { action: 'click->dialog#load',
 *                  dialog_url_param: '/some/url/to/load',
 *                  dialog_auto_open_value: 'true' }}
 *    Open dialog and load content from a URL
 *
 *  %dialog{id: 'fancy-dialog', data: { dialog_target: :dialog }}
 *  %div{data: { dialog_target: :title }}Dialog title
 *    %button{type: :button, data: { action: 'click->dialog#close' }} x
 *
 *    %div{data: { dialog_target: :content }} Dialog content
 *
 * The dialog is opened immediately with a loading spinner. Once the request is
 * resolved, the spinner is removed. The response should consist of a document
 * fragment with two nodes: `h1` and anything else. `h1` is used as the content
 * for the title target, the other element for the content target.
 *
 * Opening the dialog changes the URL hash to the dialog's ID. If the page is
 * loaded with this hash, the dialog is immediately opened. If the dialog is on
 * a tab, the tab is first send `tabs:show` event. If the dialog is in a dropdown,
 * the dropdown is first sent `dropdown:show` event.
 *
 * The dialog dispatches `dialog:loaded` event when the content is loaded.
 *
 * A dialog is page state, not navigation. Opening and closing one overwrites
 * the current history entry instead of stacking onto it, so Back always leaves
 * the page rather than stepping through dialog hashes. Closing restores the
 * previous hash, or the fallback hash value when there was none.
 *
 * If autoOpen is true, the dialog is opened immediately after the controlled
 * element connects.
 *
 * The dialog also listens to `dialog:close` event and closes itself when
 * received.
 *
 *  - If the event details has the key `current`, the preview target's
 *    outerHTML is replaced by it.
 *  - If the event details has the key `previewId`, the preview target's
 *    outerHTML is replaced the content of the element with `previewId`.
 *
 * This allows to update a bit of DOM with a result of a form submission
 * in the dialog.
 *
 * When the dialog is opened, a CSS class `overflow-hidden` is applied to
 * `body`. It gets removed once the last dialog is closed.
 *
 */
export default class extends Controller {
  static values = {
    fallbackHash: String,
    autoOpen: { type: Boolean, default: false },
    // Linkable dialogs deep-link through the URL hash: opening writes the hash
    // and a page load with that hash auto-opens them. Transient dialogs (e.g.
    // the per-node sidebar context menu) set linkable:false - their hash carries
    // no entity identity, so it can't recreate state, and re-opening on every
    // re-render of their markup is unwanted.
    linkable: { type: Boolean, default: true },
  }
  static targets = ["dialog", "title", "content", "preview", "closer", "form"]

  connect() {
    super.connect()

    fetches(this)

    // A dialog restored from a Turbo snapshot carries `open` without ever having
    // gone through showModal(), so it is not modal - no backdrop, no focus trap -
    // and load() would refuse to reopen it. Reset it and let the hash below
    // decide whether it opens for real.
    // The body class is restored with the snapshot too, so it has to go with it.
    if (this.dialogTarget.open) {
      this.dialogTarget.close()
      this._releaseBodyScroll()
    }

    if (!this.linkableValue) {
      return
    }

    // Back and Forward between two hashes of the same page do not re-render, so
    // connect() does not run again. The hash is the only signal we get.
    this.hashChangeListener = this._onHashChange.bind(this)
    window.addEventListener("hashchange", this.hashChangeListener)

    if (window.location.hash !== this._dialogUrlHash() && !this.autoOpenValue) {
      return
    }

    this._maybeShowOpener()
    this._activateDialogFromUrlAnchor()
    this.eventStartedInsideDialog = false
  }

  disconnect() {
    super.disconnect()
    window.removeEventListener("hashchange", this.hashChangeListener)
    if (this.keydownHandler) {
      this.element.removeEventListener("keydown", this.keydownHandler)
    }
    if (this.closeEventHandler) {
      this.dialogTarget.removeEventListener("dialog:close", this.closeEventHandler)
    }
  }

  /* The hash moved. Follow it: open when it becomes ours, close when it leaves.
     Neither writes history - the URL is already where the user put it. */
  _onHashChange() {
    const isOurs = window.location.hash === this._dialogUrlHash()

    if (isOurs && !this.dialogTarget.open) {
      this._maybeShowOpener()
      this._activateDialogFromUrlAnchor()
    } else if (!isOurs && this.dialogTarget.open) {
      this._closeKeepingUrl()
    }
  }

  closerTargetConnected() {
    this.close()
  }

  close() {
    this._closeKeepingUrl()

    if (this.linkableValue) {
      this._restoreUrlHash()
    }
  }

  /* Close and notify, but leave the URL alone - for when the URL has already
     moved on, e.g. the user pressed Back. */
  _closeKeepingUrl() {
    const closeEvent = new Event("dialog:closed", { bubbles: true })
    this.element.dispatchEvent(closeEvent)

    this._removeCloseWhenClickedOutsideListener()
    this._removeSaveEventStartedInsideDialog()
    this.dialogTarget.close()
    this.autoOpenValue = false

    this._releaseBodyScroll()
  }

  /* Body scroll stays locked while any dialog is still open. */
  _releaseBodyScroll() {
    if (document.querySelectorAll("dialog[open]").length === 0) {
      document.body.classList.remove(this.bodyClass())
    }
  }

  /* Put back the hash the page had before this dialog opened, or the tab this
     dialog belongs to. */
  _restoreUrlHash() {
    const id = this.element.dataset.returnHash || this.fallbackHashValue
    const hash = id ? `#${id}` : ""

    replaceUrl(`${window.location.pathname}${window.location.search}${hash}`)
  }

  open(event) {
    event.preventDefault()
    if (this.dialogTarget.open) {
      return
    }
    this._showModal()
  }

  load(event) {
    event.preventDefault()
    if (this.dialogTarget.open) {
      return
    }

    const url = event.params.url
    if (!url) {
      return console.error("missing URL param to load")
    }

    this._saveUrlHash()
    this.showSpinner()
    this._showModal()

    this.fetchPartial(url).then((res) => {
      this.hideSpinner()
      if (res.redirectHandled) return
      if (res.applicationError) {
        this.contentTarget.textContent = res.applicationError.message
        return
      }

      res.text().then((text) => this._insertContent(text))
    })

    this._addCloseWhenClickedOutsideListener()
    this._addSaveEventStartedInsideDialog()
  }

  // Replaces the content of the currently open dialog with content loaded from a new URL.
  // Use this instead of close+reopen when navigating between "screens" within the same dialog.
  async replaceWith(event) {
    event.preventDefault()
    const url = event.params.url
    if (!url) {
      return console.error("missing URL param to load")
    }

    this.showSpinner()
    try {
      const res = await this.fetchPartial(url)
      this.hideSpinner()
      if (res.redirectHandled) return
      if (res.applicationError) {
        this.contentTarget.textContent = res.applicationError.message
        return
      }

      this._insertContent(await res.text())
    } catch (error) {
      this.hideSpinner()
      console.error("replaceWith failed", { url, error })
    }
  }

  // Parses an HTML partial and inserts its h1 into titleTarget and the rest into contentTarget.
  // The response is expected to be either:
  //   <h1>…</h1><div>…</div>  => title + content
  //   anything else           => inserted as-is into contentTarget
  _insertContent(text) {
    const doc = new DOMParser().parseFromString(text, "text/html")
    const heading = doc.querySelector("body > h1")
    if (heading) {
      this.titleTarget.innerHTML = heading.outerHTML
      this.contentTarget.innerHTML = doc.querySelector("h1 ~ *").outerHTML
    } else {
      this.contentTarget.innerHTML = doc.documentElement.outerHTML
    }
    window.dispatchEvent(new CustomEvent("dialog:loaded"))
  }

  _saveUrlHash() {
    if (window.location.hash === this._dialogUrlHash()) {
      return
    }
    this.element.dataset.returnHash = window.location.hash.slice(1)
  }

  _showModal() {
    document.body.classList.add(this.bodyClass())

    this.dialogTarget.showModal()

    this.keydownHandler = this._closeOnEscape.bind(this)
    this.element.addEventListener("keydown", this.keydownHandler)

    this.closeEventHandler = this._closeEvent.bind(this)
    this.dialogTarget.addEventListener("dialog:close", this.closeEventHandler)

    if (this.linkableValue) {
      // Assigning location.hash makes browsers scroll to the element with this
      // dialog's ID. In Chrome, that scroll affects the page behind a modal.
      // Keep the deep-link in the address bar, but do not perform anchor
      // navigation.
      replaceUrl(this._dialogUrlHash())
    }
  }

  _dialogUrlHash() {
    return `#${this.dialogTarget.id}`
  }

  _closeEvent(event) {
    if (event.detail && event.detail.current && this.hasPreviewTarget) {
      this.previewTarget.outerHTML = event.detail.current
    } else if (event.detail && event.detail.previewId && this.hasPreviewTarget) {
      const previewTemplate = document.getElementById(event.detail.previewId)
      if (previewTemplate) {
        this.previewTarget.outerHTML = previewTemplate.innerHTML
        previewTemplate.remove()
      }
    }
    this.close()
  }

  _closeOnEscape(event) {
    if (event.key === "Escape" || event.keyCode === 27) {
      this.close()
    }
  }

  _activateDialogFromUrlAnchor() {
    const opener = this.element.querySelector('[data-action*="dialog#load"], [data-action*="dialog#open"]')
    if (opener && opener.checkVisibility()) {
      opener.click()
    } else {
      console.error(`No dialog opener found for ${this._dialogUrlHash()}`)
      this._restoreUrlHash()
    }
  }

  _maybeShowOpener() {
    this.element.dispatchEvent(new CustomEvent("dialog:connected", { bubbles: true }))
  }

  hideSpinner() {
    this._unlockDialogHeight()
    this.dialogTarget.dataset.controller = (this.dialogTarget.dataset.controller || "")
      .split(" ")
      .filter((str) => str && str !== "spinner")
      .join(" ")
  }

  _lockDialogHeight() {
    if (this._previousDialogMinHeight !== undefined) return

    this._previousDialogMinHeight = this.dialogTarget.style.minHeight
    this.dialogTarget.style.minHeight = `${this.dialogTarget.getBoundingClientRect().height}px`
  }

  _unlockDialogHeight() {
    if (this._previousDialogMinHeight === undefined) return

    this.dialogTarget.style.minHeight = this._previousDialogMinHeight
    this._previousDialogMinHeight = undefined
  }

  showSpinner() {
    this._lockDialogHeight()
    this.dialogTarget.dataset.controller = `${this.dialogTarget.dataset.controller || ""} spinner`.trim()
  }

  hideSpinnerIfFailed(event) {
    if (event.detail?.success) return
    this.hideSpinner()
  }

  _addCloseWhenClickedOutsideListener() {
    this._boundCloseWhenClickedOutside = this._boundCloseWhenClickedOutside || this._closeWhenClickedOutsideListener()
    this.dialogTarget.addEventListener("click", this._boundCloseWhenClickedOutside)
  }

  _removeCloseWhenClickedOutsideListener() {
    if (this._boundCloseWhenClickedOutside) {
      this.dialogTarget.removeEventListener("click", this._boundCloseWhenClickedOutside)
      this._boundCloseWhenClickedOutside = null
    }
  }

  _addSaveEventStartedInsideDialog() {
    this._boundSaveEventStarted = this._boundSaveEventStarted || this._saveEventStartedInsideDialog()
    this.dialogTarget.addEventListener("mousedown", this._boundSaveEventStarted)
  }

  _removeSaveEventStartedInsideDialog() {
    if (this._boundSaveEventStarted) {
      this.dialogTarget.removeEventListener("mousedown", this._boundSaveEventStarted)
      this._boundSaveEventStarted = null
    }
  }

  _isEventWithinDialog(event) {
    if (this._isClickThroughKeyboard(event)) return true
    if (this._isEventWithinDialogChild(event)) return true

    const dialogRect = this.dialogTarget.getBoundingClientRect()
    return (
      dialogRect.x <= event.x &&
      event.x <= dialogRect.x + dialogRect.width &&
      dialogRect.y <= event.y &&
      event.y <= dialogRect.y + dialogRect.height
    )
  }

  _isEventWithinDialogChild(event) {
    return event.target !== this.dialogTarget && this.dialogTarget.contains(event.target)
  }

  _isClickThroughKeyboard(event) {
    return event.x === 0 && event.y === 0
  }

  _saveEventStartedInsideDialog() {
    return (event) => {
      this.eventStartedInsideDialog = this._isEventWithinDialog(event)
    }
  }

  _closeWhenClickedOutsideListener() {
    return (event) => {
      if (!event.target.checkVisibility()) return
      if (this._isEventWithinDialog(event) || this.eventStartedInsideDialog) {
        return
      }
      this.close()
    }
  }

  bodyClass() {
    return "overflow-hidden"
  }
}
